import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { type SyntheticEvent, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Arrange, Link, Typography, useToast } from "@aviary";
import FsEeDualLogoIcon from "@shared/assets/images/fs-ee-dual-logo-icon.svg";
import { AuthHeader } from "@shared/components/AuthHeader/AuthHeader";
import { AuthHeaderWrapper } from "@shared/components/AuthHeaderWrapper/AuthHeaderWrapper";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import type { StorePlatform } from "@shared/types/StorePlatform";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import type { RequestHarmonizedMigrationVerificationEmailUnauthenticatedSignUpSignInMutation as RequestHarmonizedMigrationVerificationEmailData } from "@unauthenticated/signupSignin/data/mutations/RequestHarmonizedMigrationVerificationEmail.mutation.generated";
import { useRequestHarmonizedMigrationVerificationEmailUnauthenticatedSignUpSignInMutation as useRequestHarmonizedMigrationVerification } from "@unauthenticated/signupSignin/data/mutations/RequestHarmonizedMigrationVerificationEmail.mutation.generated";
import type { RequestSharedAccountVerificationEmailUnauthenticatedSignUpSignInMutation as RequestSharedAccountVerificationEmailData } from "@unauthenticated/signupSignin/data/mutations/RequestSharedAccountVerificationEmail.mutation.generated";
import { useRequestSharedAccountVerificationEmailUnauthenticatedSignUpSignInMutation as useRequestSharedAccountVerification } from "@unauthenticated/signupSignin/data/mutations/RequestSharedAccountVerificationEmail.mutation.generated";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { Page } from "../../components/Page/Page";
import { PageContent } from "../../components/PageContent/PageContent";

import * as styles from "./LinkExpiredNotificationPage.styles";

interface Props {
  email: string;
  storePlatform: StorePlatform;
  migrateHarmonizedAccount?: boolean;
}

const LinkExpiredNotificationPage = ({
  email,
  storePlatform,
  migrateHarmonizedAccount = false,
}: Props) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const isDesktop = tablet.greaterThan;
  const { makeToast } = useToast();
  const [requestSharedAccountVerification] = useRequestSharedAccountVerification({
    onCompleted: (data: RequestSharedAccountVerificationEmailData) => {
      const isSuccessful = data?.auth?.requestSharedAccountVerificationEmail?.sent;
      if (!isSuccessful) {
        makeToast("error", t(l.linkNotification.errorResendLink));
      }
    },
    onError: error => {
      makeToast("error", error?.message || t(l.linkNotification.errorResendLink));
    },
  });
  const [requestHarmonizedMigrationVerification] = useRequestHarmonizedMigrationVerification({
    onCompleted: (data: RequestHarmonizedMigrationVerificationEmailData) => {
      const isSuccessful = data?.auth?.requestHarmonizedMigrationVerificationEmail?.sent;
      if (!isSuccessful) {
        makeToast("error", t(l.linkNotification.errorResendLink));
      }
    },
    onError: error => {
      makeToast("error", error?.message || t(l.linkNotification.errorResendLink));
    },
  });

  const migrateHarmonizedAccountQuery = () => {
    makeToast("success", t(l.linkNotification.newResendLink));
    gRecaptchaExecute(gToken => {
      requestHarmonizedMigrationVerification({
        variables: {
          input: {
            email,
            captchaToken: gToken,
          },
        },
      });
    });
  };

  const sharedAccountQuery = () => {
    makeToast("success", t(l.linkNotification.newResendLink));
    gRecaptchaExecute(gToken => {
      requestSharedAccountVerification({
        variables: {
          input: {
            email,
            requestedPlatform: storePlatform,
            captchaToken: gToken,
          },
        },
      });
    });
  };

  useEffect(() => {
    migrateHarmonizedAccount ? migrateHarmonizedAccountQuery() : sharedAccountQuery();
  }, []);

  const resendLink = (e: SyntheticEvent) => {
    e.preventDefault();
    migrateHarmonizedAccount ? migrateHarmonizedAccountQuery() : sharedAccountQuery();
  };

  const platform = () => (storePlatform === "Fullscript" ? "fs" : "ee");
  const renderResendLink = () => (
    <Link onClick={resendLink} css={styles.inlineLink} data-testid="resend-link">
      {t(l.linkNotification.resend)}
    </Link>
  );

  return (
    <Page data-e2e="link-expired-notification">
      <AuthHeaderWrapper includeBorder>
        <AuthHeader
          route={storePlatform === "Fullscript" ? authRoutes.login : authRoutes.emerson.login}
          showFromEmerson={storePlatform === "Emerson"}
        />
      </AuthHeaderWrapper>
      <PageContent css={styles.notificationWrapper}>
        <Arrange justify="center" alignment="center" isFullWidth isFullHeight isVertical>
          <Typography isFullWidth type={isDesktop ? "h1" : "h3"}>
            {t(l.linkNotification.newSignInLink)}
          </Typography>
          <Typography type={isDesktop ? "h4" : "body"} css={styles.semibold}>
            {t(l.linkNotification.pleaseCheck[platform()])}
          </Typography>
          <Arrange justify="center" isFullWidth>
            <img
              css={styles.dualLogoIcon}
              src={FsEeDualLogoIcon}
              alt="Fullscript Emerson Ecologics logo"
            />
          </Arrange>
          <Typography type="body" css={styles.bodySpacing}>
            <Trans
              i18nKey={l.linkNotification.emailedTo}
              values={{ email }}
              components={{ bold: <strong /> }}
            />
          </Typography>
          <Typography type="body">
            <Trans
              i18nKey={l.linkNotification.didntGetEmail}
              components={{
                1: renderResendLink(),
              }}
            >
              {renderResendLink()}
            </Trans>
          </Typography>
          <Link
            href={
              storePlatform === "Fullscript"
                ? authRoutes.practitioner_sign_up
                : authRoutes.emerson.sign_up
            }
            css={styles.noStyleLink}
          >
            <FontAwesomeIcon
              className="icon-chevron"
              icon={faChevronLeft}
              size="sm"
              css={styles.chevron}
            />
            {t(l.linkNotification.returnToSignUp)}
          </Link>
        </Arrange>
      </PageContent>
    </Page>
  );
};

export { LinkExpiredNotificationPage };
